/**
 * PHARMAKON — Cross-module X · XWP-18 — Caisse libre multiwindow store (brief §10; 02_DESIGN §4.1).
 *
 * Hybrid persistence (XA-08): the server holds every window, its lines and their stock
 * reservations; this store keeps them in memory, mirrored to sessionStorage for an instant
 * restore after a reload, and reconciles with the server (authoritative) right after.
 *
 *   · switching windows (Maj+F1…F3, a click on a tab) is local — no request;
 *   · each line / header change is ONE small request (never the whole basket), queued per window
 *     so the versions stay ordered; a quantity typed or stepped is committed after
 *     CAISSE_FENETRES.debounce_quantite_ms (Entrée / blur commit at once);
 *   · an added product shows at once « Réservation… »; a 409 keeps the line with
 *     « Stock insuffisant — n disponibles » and the one-key fix « Ajuster à n »;
 *   · a header change carries If-Match: a stale tab gets 409 and reloads the window;
 *   · the remise being typed (XWP-19, « % | DA ») shows at once (overlay `vue.remise`) and is
 *     checkpointed after the same debounce (Entrée / blur at once); validation flushes it first and
 *     is refused while the server refused it;
 *   · validation flushes the pending changes, then `POST /:id/valider` with the version the
 *     cashier saw: the window becomes one sale (server side, one transaction);
 *   · the login session id lives in localStorage per user (XD-39); logout closes it
 *     (`fermerSession`, called by authStore.logout) and wipes the mirror.
 *
 * Per-window UI state that is not part of the sale (Comptant / Crédit mode, payment method and
 * reference, loyalty card, crédit card custody, the médecin object, posology prefills) lives in
 * `locaux[id]`, mirrored with the rest.
 */
import { create } from "zustand";
import { caisseFenetreApi } from "../services/caisse-fenetre.api.js";
import { CAISSE_FENETRES, MESSAGES_FENETRES } from "@pharmakon/shared/constants/caisse-fenetres.js";
import {
  cleMiroir,
  cleSession,
  estFenetrePerdue,
  fenetresOrdonnees,
  lireRefus,
  nouvelleCleLigne,
  prixOptimiste,
} from "../lib/caisse-fenetres.js";
import { memeRemise } from "../lib/remise-vente-saisie.js";

const donnees = (r) => r?.data ?? r;

// ── Module-level runtime (never persisted): per-window queues, quantity timers ───────────────────
const files = new Map(); // fenetre_id → Promise (the chain of its writes)
const minuteries = new Map(); // `${fenetre_id}:${cle}` → timeout id
const minuteriesRemise = new Map(); // fenetre_id → timeout id of the remise checkpoint (XWP-19)

/** Run `travail` after every queued write of the window (one write at a time per window). */
function enFile(id, travail) {
  const precedente = files.get(id) ?? Promise.resolve();
  const suivante = precedente.catch(() => {}).then(travail);
  files.set(id, suivante.catch(() => {}));
  return suivante;
}

function lireStockage(store, cle) {
  try {
    return store?.getItem(cle) ?? null;
  } catch {
    return null;
  }
}
function ecrireStockage(store, cle, valeur) {
  try {
    if (valeur == null) store?.removeItem(cle);
    else store?.setItem(cle, valeur);
  } catch {
    /* private mode, quota — the server stays the source of truth */
  }
}
const local = () => (typeof window !== "undefined" ? window.localStorage : null);
const session = () => (typeof window !== "undefined" ? window.sessionStorage : null);

// `remise`: { valeur: { mode, valeur } | null } while a typed remise is not committed yet (XWP-19);
// `erreurRemise`: the server's refusal of the last remise checkpoint (blocks the validation).
const vueVide = () => ({ quantites: {}, attente: {}, erreurs: {}, nouvelles: [], meta: {}, remise: null, erreurRemise: null });
const locauxVides = () => ({
  mode: "LIBRE",
  methode_paiement: "ESPECES",
  reference_paiement: "",
  fidelite: { numero: "", carte: null, points: 0 },
  credit: { carte_chifa_retenue: false, card_type: "CHIFA", carte_numero: "", notes: "" },
  medecin: null,
  // XWP-19 — the unit the « Remise » field shows while the window has no remise (% | DA)
  remise_mode: null,
});

const etatInitial = () => ({
  userId: null,
  sessionId: null,
  statut: "inactif", // inactif · chargement · pret · remplacee · erreur
  erreur: null,
  fenetres: {}, // id → server DTO (ACTIVE windows)
  activeId: null,
  expirees: [], // windows of this session closed by the inactivity TTL (tabs « Expirée »)
  vues: {}, // id → overlay (pending quantities, states, refusals, optimistic lines, line meta)
  locaux: {}, // id → per-window UI state
});

/** Close the login session of `userId` (logout), whatever page is mounted. Never throws. */
export async function fermerSessionCaisse(userId) {
  if (!userId) return;
  const sid = lireStockage(local(), cleSession(userId));
  ecrireStockage(local(), cleSession(userId), null);
  ecrireStockage(session(), cleMiroir(userId), null);
  for (const t of minuteries.values()) clearTimeout(t);
  minuteries.clear();
  for (const t of minuteriesRemise.values()) clearTimeout(t);
  minuteriesRemise.clear();
  files.clear();
  useCaisseFenetresStore.setState(etatInitial());
  if (!sid) return;
  try {
    await caisseFenetreApi.fermerSession(sid);
  } catch {
    /* the inactivity TTL releases the holds anyway (XD-24) */
  }
}

export const useCaisseFenetresStore = create((set, get) => {
  // ── internal helpers ────────────────────────────────────────────────────────────────────────
  const vue = (id) => get().vues[id] ?? vueVide();
  const majVue = (id, fn) => set((s) => ({ vues: { ...s.vues, [id]: fn(s.vues[id] ?? vueVide()) } }));

  /** Install the server DTO of a window (ACTIVE → kept; otherwise removed / listed as expired). */
  const installer = (dto) => {
    if (!dto) return;
    set((s) => {
      const fenetres = { ...s.fenetres };
      if (dto.statut === "ACTIVE" && dto.rang != null) fenetres[dto.id] = dto;
      else delete fenetres[dto.id];
      const expirees =
        dto.statut === "EXPIREE" && !s.expirees.some((e) => e.id === dto.id) ? [...s.expirees, dto] : s.expirees;
      return { fenetres, expirees };
    });
  };

  /**
   * Put a line DTO answered by the server into its window — X XWP-98 (XO-26 (1)): with the other lines
   * the same change wrote (`lignes_liees`: a quantity spread over two lot rows, one line per row).
   */
  const installerLigne = (id, ligne, fen, liees = []) => {
    set((s) => {
      const f = s.fenetres[id];
      if (!f) return {};
      let lignes = f.lignes;
      for (const l of [ligne, ...(liees ?? [])].filter(Boolean)) {
        lignes = lignes.some((x) => x.cle === l.cle) ? lignes.map((x) => (x.cle === l.cle ? l : x)) : [...lignes, l];
      }
      return { fenetres: { ...s.fenetres, [id]: { ...f, ...(fen ?? {}), lignes } } };
    });
  };

  /** X XWP-98 — a line spread over another lot row's line: that line inherits the local display data. */
  const heriterMeta = (id, cle, liees) => {
    if (!liees?.length) return;
    majVue(id, (v) => {
      const meta = { ...v.meta };
      for (const l of liees) if (!meta[l.cle] && meta[cle]) meta[l.cle] = meta[cle];
      return { ...v, meta };
    });
  };

  /** A refusal that means the window is gone server side: reload it (EXPIREE tab, or removed). */
  const perdue = async (id, refus) => {
    if (refus.code === "SESSION_CAISSE_REMPLACEE") {
      set({ statut: "remplacee", erreur: refus.message });
      return;
    }
    try {
      installer(donnees(await caisseFenetreApi.obtenir(id)));
    } catch {
      set((s) => {
        const fenetres = { ...s.fenetres };
        delete fenetres[id];
        return { fenetres };
      });
    }
    get()._choisirActive();
  };

  /** A version conflict (another tab wrote first): reload the window, keep the cashier informed. */
  const recharger = async (id) => {
    try {
      installer(donnees(await caisseFenetreApi.obtenir(id)));
    } catch {
      /* keep what we have */
    }
  };

  return {
    ...etatInitial(),

    /** Keep the active window valid; open a fresh one when none is left. */
    _choisirActive: async () => {
      const { fenetres, activeId } = get();
      if (activeId && fenetres[activeId]) return;
      const ordre = fenetresOrdonnees(fenetres);
      if (ordre.length) {
        set({ activeId: ordre[0].id });
        return;
      }
      if (get().statut === "pret") await get().nouvelleFenetre();
    },

    /**
     * Mount of the till: restore the tab's mirror at once, then reconcile with the server
     * (authoritative). A session replaced by a newer login is never silently stolen back: the
     * till shows « Reprendre la caisse ici ».
     */
    initialiser: async (userId) => {
      if (!userId) return;
      if (get().userId !== userId) set({ ...etatInitial(), userId });
      const miroir = lireStockage(session(), cleMiroir(userId));
      if (miroir && !Object.keys(get().fenetres).length) {
        try {
          const m = JSON.parse(miroir);
          set({
            sessionId: m.sessionId ?? null,
            fenetres: m.fenetres ?? {},
            activeId: m.activeId ?? null,
            expirees: m.expirees ?? [],
            vues: Object.fromEntries(
              Object.entries(m.vues ?? {}).map(([k, v]) => [k, { ...vueVide(), meta: v.meta ?? {}, quantites: v.quantites ?? {}, remise: v.remise ?? null }]),
            ),
            locaux: m.locaux ?? {},
          });
        } catch {
          /* a broken mirror is ignored — the server answers below */
        }
      }
      set({ statut: "chargement", erreur: null });
      const sid = lireStockage(local(), cleSession(userId));
      try {
        if (!sid) {
          await get().ouvrirSession();
          return;
        }
        const liste = donnees(await caisseFenetreApi.lister(sid));
        if (!liste.session_courante) {
          set({ statut: "remplacee", erreur: MESSAGES_FENETRES.SESSION_REMPLACEE, fenetres: {}, activeId: null });
          return;
        }
        const fenetres = Object.fromEntries(liste.fenetres.map((f) => [f.id, f]));
        set((s) => ({
          sessionId: sid,
          fenetres,
          expirees: liste.expirees.filter((e) => s.expirees.some((x) => x.id === e.id) || Date.now() - new Date(e.ferme_at).getTime() < CAISSE_FENETRES.ttl_inactivite_minutes * 60_000),
          statut: "pret",
        }));
        // pending quantities and remise restored from the mirror are committed again
        for (const [id, v] of Object.entries(get().vues)) {
          if (!fenetres[id]) continue;
          for (const cle of Object.keys(v.quantites ?? {})) get().validerQuantite(id, cle);
          if (v.remise) get().validerRemise(id);
        }
        await get()._choisirActive();
      } catch (e) {
        set({ statut: "erreur", erreur: lireRefus(e, "Caisse indisponible — vérifiez la connexion.").message });
      }
    },

    /** Open a new login session (first mount, or « Reprendre la caisse ici »): window 1. */
    ouvrirSession: async () => {
      const { userId } = get();
      set({ statut: "chargement", erreur: null });
      try {
        const r = donnees(await caisseFenetreApi.ouvrirSession());
        ecrireStockage(local(), cleSession(userId), r.session_poste_id);
        const fenetres = Object.fromEntries(r.fenetres.map((f) => [f.id, f]));
        set({ sessionId: r.session_poste_id, fenetres, activeId: r.fenetres[0]?.id ?? null, expirees: [], vues: {}, locaux: {}, statut: "pret" });
      } catch (e) {
        set({ statut: "erreur", erreur: lireRefus(e, "Ouverture de la caisse impossible.").message });
      }
    },

    // ── windows ────────────────────────────────────────────────────────────────────────────────

    /** F3 « Nouvelle fenêtre (client suivant) ». Returns an error message or null. */
    nouvelleFenetre: async () => {
      const { sessionId } = get();
      if (!sessionId) return MESSAGES_FENETRES.SESSION_REMPLACEE;
      if (fenetresOrdonnees(get().fenetres).length >= CAISSE_FENETRES.max_actives) return MESSAGES_FENETRES.LIMITE;
      try {
        const r = donnees(await caisseFenetreApi.ouvrir(sessionId));
        installer(r.fenetre);
        set({ activeId: r.fenetre.id });
        return null;
      } catch (e) {
        const refus = lireRefus(e);
        if (refus.code === "SESSION_CAISSE_REMPLACEE") set({ statut: "remplacee", erreur: refus.message });
        return refus.message;
      }
    },

    /** Switch window — local only (02_DESIGN §4.1: no request). */
    activer: (id) => {
      if (get().fenetres[id]) set({ activeId: id });
    },
    /** Maj+F1…F3 — go to window n (by rang). Returns false when there is none. */
    activerRang: (rang) => {
      const f = fenetresOrdonnees(get().fenetres).find((x) => x.rang === rang);
      if (!f) return false;
      set({ activeId: f.id });
      return true;
    },

    /** Maj+Suppr « Fermer la fenêtre » (after confirmation): its holds go back to sale. */
    fermerFenetre: async (id) => {
      clearTimeout(minuteriesRemise.get(id));
      minuteriesRemise.delete(id);
      try {
        await enFile(id, () => caisseFenetreApi.fermer(id));
      } catch (e) {
        const refus = lireRefus(e);
        if (!estFenetrePerdue(refus)) return refus.message;
      }
      set((s) => {
        const fenetres = { ...s.fenetres };
        delete fenetres[id];
        const vues = { ...s.vues };
        delete vues[id];
        const locaux = { ...s.locaux };
        delete locaux[id];
        return { fenetres, vues, locaux, activeId: s.activeId === id ? null : s.activeId };
      });
      await get()._choisirActive();
      return null;
    },

    /** « Vider » — the window is closed and a fresh one takes its place. */
    viderFenetre: async (id) => {
      const msg = await get().fermerFenetre(id);
      if (msg) return msg;
      if (fenetresOrdonnees(get().fenetres).length < CAISSE_FENETRES.max_actives) {
        const err = await get().nouvelleFenetre();
        if (err) return err;
      }
      return null;
    },

    /** Forget an expired tab (« Expirée — réservations libérées »). */
    oublierExpiree: (id) => set((s) => ({ expirees: s.expirees.filter((e) => e.id !== id) })),

    // ── lines ──────────────────────────────────────────────────────────────────────────────────

    /**
     * Add a product (Entrée, scan, prefill): optimistic line « Réservation… », then one request.
     * @param {string} id
     * @param {{ produit: object, quantite?: number, stock_lot_id?: string|null, lot?: object|null,
     *           secours?: object|null, meta?: object }} p
     *   lot — the lot object the caller holds (a scan's lot): prices the optimistic line only
     * @returns {Promise<{ cle: string, ligne?: object, refus?: object }>}
     */
    ajouterLigne: async (id, { produit, quantite = 1, stock_lot_id = null, code_lot = null, lot = null, secours = null, meta = {} }) => {
      const cle = nouvelleCleLigne();
      const optimiste = {
        cle,
        produit_id: produit.id,
        designation: produit.nom_commercial ?? produit.designation ?? null,
        dosage: produit.dosage ?? null,
        quantite,
        lot_explicite: Boolean(stock_lot_id),
        secours: secours ?? null,
        // X XWP-80 (XD-228) — priced like the window DTO will be: a secours line at the counter price
        // just confirmed (XWP-37, SAISI_SECOURS), an explicit lot by the one resolver, a FEFO pick at
        // the search row's `prix_vente`; unknown otherwise until the server answers — never the
        // produit PPA, never 0 DA
        ...prixOptimiste({ produit, stock_lot_id, lot, secours }),
        ppa: produit.ppa != null ? String(produit.ppa) : null,
        psychotrope: Boolean(produit.is_psychotrope ?? produit.psychotrope),
        alerte_psychotrope: produit.alerte_psychotrope ?? null,
        _stock_lot_id: stock_lot_id,
        // X XWP-98 review — the lot label scanned for `stock_lot_id` (the server's proof, never shown)
        _code_lot: stock_lot_id ? code_lot : null,
      };
      majVue(id, (v) => ({ ...v, nouvelles: [...v.nouvelles, optimiste], meta: { ...v.meta, [cle]: meta } }));
      return get()._envoyerAjout(id, optimiste);
    },

    /** POST the line (first try or « Ajuster à n »). */
    _envoyerAjout: (id, ligne) =>
      enFile(id, async () => {
        try {
          const r = donnees(
            await caisseFenetreApi.ajouterLigne(id, {
              cle: ligne.cle,
              produit_id: ligne.produit_id,
              quantite: ligne.quantite,
              ...(ligne._stock_lot_id ? { stock_lot_id: ligne._stock_lot_id } : {}),
              ...(ligne._stock_lot_id && ligne._code_lot ? { code_lot: ligne._code_lot } : {}),
              ...(ligne.secours ? { secours: ligne.secours } : {}),
            }),
          );
          majVue(id, (v) => {
            const erreurs = { ...v.erreurs };
            delete erreurs[ligne.cle];
            return { ...v, erreurs, nouvelles: v.nouvelles.filter((n) => n.cle !== ligne.cle) };
          });
          installerLigne(id, r.ligne, r.fenetre, r.lignes_liees);
          heriterMeta(id, ligne.cle, r.lignes_liees);
          return { cle: ligne.cle, ligne: r.ligne, liees: r.lignes_liees ?? [] };
        } catch (e) {
          const refus = lireRefus(e, "Ajout impossible");
          if (refus.quantite_possible != null) {
            majVue(id, (v) => ({ ...v, erreurs: { ...v.erreurs, [ligne.cle]: refus } }));
          } else {
            majVue(id, (v) => ({ ...v, nouvelles: v.nouvelles.filter((n) => n.cle !== ligne.cle) }));
            if (estFenetrePerdue(refus)) await perdue(id, refus);
          }
          return { cle: ligne.cle, refus };
        }
      }),

    /** + / − / typed quantity: local at once, committed after the debounce (or Entrée / blur). */
    changerQuantite: (id, cle, quantite) => {
      const q = Math.max(1, Math.min(CAISSE_FENETRES.quantite_max, Math.floor(Number(quantite) || 1)));
      const nouvelle = vue(id).nouvelles.find((n) => n.cle === cle);
      if (nouvelle) {
        // a refused addition: the new quantity is simply the next try
        majVue(id, (v) => ({ ...v, nouvelles: v.nouvelles.map((n) => (n.cle === cle ? { ...n, quantite: q } : n)) }));
        return;
      }
      majVue(id, (v) => {
        const erreurs = { ...v.erreurs };
        delete erreurs[cle];
        return { ...v, quantites: { ...v.quantites, [cle]: q }, erreurs };
      });
      const k = `${id}:${cle}`;
      clearTimeout(minuteries.get(k));
      minuteries.set(
        k,
        setTimeout(() => {
          minuteries.delete(k);
          get().validerQuantite(id, cle);
        }, CAISSE_FENETRES.debounce_quantite_ms),
      );
    },

    /** Commit the pending quantity of a line now (Entrée, blur, timer, before validation). */
    validerQuantite: (id, cle) => {
      const k = `${id}:${cle}`;
      clearTimeout(minuteries.get(k));
      minuteries.delete(k);
      return enFile(id, async () => {
        const q = vue(id).quantites[cle];
        const ligne = get().fenetres[id]?.lignes.find((l) => l.cle === cle);
        if (q == null || !ligne) return null;
        if (q === ligne.quantite) {
          majVue(id, (v) => {
            const quantites = { ...v.quantites };
            delete quantites[cle];
            return { ...v, quantites };
          });
          return null;
        }
        majVue(id, (v) => ({ ...v, attente: { ...v.attente, [cle]: "maj" } }));
        try {
          const r = donnees(await caisseFenetreApi.modifierLigne(id, cle, { quantite: q }));
          installerLigne(id, r.ligne, r.fenetre, r.lignes_liees);
          heriterMeta(id, cle, r.lignes_liees);
          majVue(id, (v) => {
            const quantites = { ...v.quantites };
            if (quantites[cle] === q) delete quantites[cle];
            const attente = { ...v.attente };
            delete attente[cle];
            return { ...v, quantites, attente };
          });
          return null;
        } catch (e) {
          const refus = lireRefus(e, "Quantité refusée");
          majVue(id, (v) => {
            const attente = { ...v.attente };
            delete attente[cle];
            return { ...v, attente, erreurs: { ...v.erreurs, [cle]: refus } };
          });
          if (estFenetrePerdue(refus)) await perdue(id, refus);
          return refus;
        }
      });
    },

    /** « Ajuster à n » (Entrée on the refused line): the quantity the stock allows, or removal at 0. */
    ajusterLigne: async (id, cle) => {
      const v = vue(id);
      const refus = v.erreurs[cle];
      if (!refus || refus.quantite_possible == null) return;
      const n = refus.quantite_possible;
      const nouvelle = v.nouvelles.find((x) => x.cle === cle);
      if (nouvelle) {
        if (n <= 0) {
          majVue(id, (w) => {
            const erreurs = { ...w.erreurs };
            delete erreurs[cle];
            return { ...w, erreurs, nouvelles: w.nouvelles.filter((x) => x.cle !== cle) };
          });
          return;
        }
        const ligne = { ...nouvelle, quantite: n };
        majVue(id, (w) => {
          const erreurs = { ...w.erreurs };
          delete erreurs[cle];
          return { ...w, erreurs, nouvelles: w.nouvelles.map((x) => (x.cle === cle ? ligne : x)) };
        });
        await get()._envoyerAjout(id, ligne);
        return;
      }
      if (n <= 0) {
        await get().supprimerLigne(id, cle);
        return;
      }
      majVue(id, (w) => {
        const erreurs = { ...w.erreurs };
        delete erreurs[cle];
        return { ...w, erreurs, quantites: { ...w.quantites, [cle]: n } };
      });
      await get().validerQuantite(id, cle);
    },

    /** Suppr — remove a line (a refused addition is simply dropped). */
    supprimerLigne: async (id, cle) => {
      const v = vue(id);
      if (v.nouvelles.some((n) => n.cle === cle)) {
        majVue(id, (w) => {
          const erreurs = { ...w.erreurs };
          delete erreurs[cle];
          return { ...w, erreurs, nouvelles: w.nouvelles.filter((n) => n.cle !== cle) };
        });
        return null;
      }
      clearTimeout(minuteries.get(`${id}:${cle}`));
      minuteries.delete(`${id}:${cle}`);
      majVue(id, (w) => ({ ...w, attente: { ...w.attente, [cle]: "suppression" } }));
      return enFile(id, async () => {
        try {
          const r = donnees(await caisseFenetreApi.supprimerLigne(id, cle));
          // XWP-22 — a line of an imported instance takes the whole instance out (every key it names)
          const retirees = new Set(r.ligne?.cles_retirees ?? [cle]);
          set((s) => {
            const f = s.fenetres[id];
            if (!f) return {};
            const instances = r.ligne?.instance_id ? (f.instances ?? []).filter((i) => i.id !== r.ligne.instance_id) : f.instances;
            return { fenetres: { ...s.fenetres, [id]: { ...f, ...r.fenetre, instances, lignes: f.lignes.filter((l) => !retirees.has(l.cle)) } } };
          });
          majVue(id, (w) => {
            const strip = (o) => {
              const c = { ...o };
              for (const k of retirees) delete c[k];
              return c;
            };
            return { ...w, attente: strip(w.attente), erreurs: strip(w.erreurs), quantites: strip(w.quantites), meta: strip(w.meta) };
          });
          return null;
        } catch (e) {
          const refus = lireRefus(e, "Suppression impossible");
          majVue(id, (w) => {
            const attente = { ...w.attente };
            delete attente[cle];
            return { ...w, attente };
          });
          if (estFenetrePerdue(refus)) await perdue(id, refus);
          return refus.message;
        }
      });
    },

    /** F7 « Changer le lot » — the line's holds move to that lot (409 when it cannot hold them). */
    changerLot: (id, cle, stock_lot_id, via = null) =>
      enFile(id, async () => {
        try {
          // X XWP-98 review — `via`: which counter action moved the lot (the server's two gates)
          const r = donnees(await caisseFenetreApi.modifierLigne(id, cle, { stock_lot_id, ...(via ? { via } : {}) }));
          installerLigne(id, r.ligne, r.fenetre, r.lignes_liees);
          return null;
        } catch (e) {
          const refus = lireRefus(e, "Changement de lot impossible");
          if (estFenetrePerdue(refus)) await perdue(id, refus);
          return refus.message;
        }
      }),

    /** Ordonnancier / posologie / durée of a line (the sale records them). */
    majDispensation: (id, cle, changements) =>
      enFile(id, async () => {
        try {
          const r = donnees(await caisseFenetreApi.modifierLigne(id, cle, changements));
          installerLigne(id, r.ligne, r.fenetre, r.lignes_liees);
          return null;
        } catch (e) {
          const refus = lireRefus(e, "Enregistrement impossible");
          if (estFenetrePerdue(refus)) await perdue(id, refus);
          return refus.message;
        }
      }),

    /** Local display data of a line not carried by the server (posology prefill, DCI, forme). */
    majMeta: (id, cle, patch) => majVue(id, (v) => ({ ...v, meta: { ...v.meta, [cle]: { ...(v.meta[cle] ?? {}), ...patch } } })),

    // ── header ─────────────────────────────────────────────────────────────────────────────────

    /**
     * Header checkpoint (client_id, remise, notes, medecin_id, ordonnance_id, commande_id), If-Match.
     * A stale version reloads the window and reports it.
     */
    majEntete: (id, changements) =>
      enFile(id, async () => {
        const f = get().fenetres[id];
        if (!f) return MESSAGES_FENETRES.NON_ACTIVE;
        try {
          const r = donnees(await caisseFenetreApi.checkpoint(id, changements, f.version));
          installer(r.fenetre);
          return null;
        } catch (e) {
          const refus = lireRefus(e, "Enregistrement impossible");
          if (refus.code === "FENETRE_CAISSE_VERSION") await recharger(id);
          else if (estFenetrePerdue(refus)) await perdue(id, refus);
          return refus.message;
        }
      }),

    /**
     * XWP-19 — the remise typed in the « Remise » field (`{ mode, valeur }`, or null to clear it):
     * shown at once (overlay), checkpointed after the debounce — the last value wins.
     */
    saisirRemise: (id, remise) => {
      if (!get().fenetres[id]) return;
      majVue(id, (v) => ({ ...v, remise: { valeur: remise ?? null }, erreurRemise: null }));
      clearTimeout(minuteriesRemise.get(id));
      minuteriesRemise.set(
        id,
        setTimeout(() => {
          minuteriesRemise.delete(id);
          get().validerRemise(id);
        }, CAISSE_FENETRES.debounce_quantite_ms),
      );
    },

    /**
     * Commit the typed remise now (Entrée, blur, timer, before validation): one header checkpoint
     * with If-Match. A refusal keeps the typed value with its message (the validation waits for a
     * correction); a stale version reloads the window. Returns the refusal message or null.
     */
    validerRemise: (id) => {
      clearTimeout(minuteriesRemise.get(id));
      minuteriesRemise.delete(id);
      return enFile(id, async () => {
        const saisie = vue(id).remise;
        const f = get().fenetres[id];
        if (!saisie || !f) return null;
        const finir = (patch) =>
          majVue(id, (v) => (v.remise && memeRemise(v.remise.valeur, saisie.valeur) ? { ...v, remise: null, ...patch } : v));
        if (memeRemise(saisie.valeur, f.remise)) {
          finir({ erreurRemise: null });
          return null;
        }
        try {
          const r = donnees(await caisseFenetreApi.checkpoint(id, { remise: saisie.valeur }, f.version));
          installer(r.fenetre);
          finir({ erreurRemise: null });
          return null;
        } catch (e) {
          const refus = lireRefus(e, "Remise refusée");
          if (estFenetrePerdue(refus)) {
            await perdue(id, refus);
            return refus.message;
          }
          // Another tab wrote first (409 version): the window is reloaded and the typed remise stays
          // on screen with the message — never written over the other change silently; Entrée in the
          // field sends it again on the new version.
          if (refus.code === "FENETRE_CAISSE_VERSION") await recharger(id);
          majVue(id, (v) => ({ ...v, erreurRemise: refus.message }));
          return refus.message;
        }
      });
    },

    /** Per-window UI state (mode, payment, loyalty card, crédit custody, médecin object). */
    majLocaux: (id, patch) =>
      set((s) => ({ locaux: { ...s.locaux, [id]: { ...locauxVides(), ...(s.locaux[id] ?? {}), ...patch } } })),
    locauxDe: (id) => ({ ...locauxVides(), ...(get().locaux[id] ?? {}) }),

    /** Commit every pending change of the window and wait for its queue. */
    vider: async (id) => {
      for (const k of [...minuteries.keys()]) {
        if (!k.startsWith(`${id}:`)) continue;
        get().validerQuantite(id, k.slice(id.length + 1));
      }
      if (vue(id).remise) get().validerRemise(id);
      await (files.get(id) ?? Promise.resolve());
    },

    // ── the sale ───────────────────────────────────────────────────────────────────────────────

    /**
     * F9 → outcome. Flushes, refuses while a line is refused or still being reserved, then
     * `POST /valider` with the version the cashier saw. On success the window leaves the bar
     * (a fresh one opens when it was the last).
     * @returns {Promise<{ vente: object, fenetre: object, lignes: Array }>} throws the refusal
     */
    valider: async (id, corps) => {
      await get().vider(id);
      const v = vue(id);
      if (v.nouvelles.length || Object.keys(v.erreurs).length) {
        throw { message: "Corrigez d'abord les lignes signalées (« Ajuster » ou Suppr)." };
      }
      if (v.erreurRemise) throw { code: "REMISE_VENTE_INVALIDE", message: v.erreurRemise };
      const f = get().fenetres[id];
      if (!f) throw { message: MESSAGES_FENETRES.NON_ACTIVE };
      const lignes = f.lignes.map((l) => ({ ...l, ...(v.meta[l.cle] ?? {}) }));
      let r;
      try {
        r = donnees(await enFile(id, () => caisseFenetreApi.valider(id, corps, f.version)));
      } catch (e) {
        const refus = lireRefus(e, "Erreur lors de la validation");
        if (refus.code === "FENETRE_CAISSE_VERSION") await recharger(id);
        else if (refus.code === "FENETRE_CAISSE_NON_ACTIVE" || refus.code === "SESSION_CAISSE_REMPLACEE") await perdue(id, refus);
        throw refus;
      }
      set((s) => {
        const fenetres = { ...s.fenetres };
        delete fenetres[id];
        const vues = { ...s.vues };
        delete vues[id];
        const locaux = { ...s.locaux };
        delete locaux[id];
        return { fenetres, vues, locaux, activeId: s.activeId === id ? null : s.activeId };
      });
      await get()._choisirActive();
      return {
        vente: r.vente,
        fenetre: r.fenetre,
        lignes,
        avertissements: r.vente?.warnings ?? [],
        // XWP-22 — the instances this sale paid in vente libre
        instances_reglees: r.instances_reglees ?? [],
      };
    },

    /**
     * X §8.1 (XWP-22) — « Envoyer vente libre »: the instance's lines enter the ACTIVE window, locked
     * (the server verified it under the instance's lock; an empty window takes its client). Pending
     * changes are flushed first so the import lands on the window the cashier sees.
     * @returns {Promise<null | { code, message, verification }>} the refusal, or null
     */
    importerInstance: async (instance_id) => {
      const id = get().activeId;
      if (!id || !get().fenetres[id]) return { code: "FENETRE_CAISSE_NON_ACTIVE", message: MESSAGES_FENETRES.NON_ACTIVE, verification: null };
      await get().vider(id);
      try {
        const r = donnees(await enFile(id, () => caisseFenetreApi.importerInstance(id, instance_id)));
        installer(r.fenetre);
        return null;
      } catch (e) {
        const refus = lireRefus(e, "Import de l'instance impossible");
        if (estFenetrePerdue(refus)) await perdue(id, refus);
        return refus;
      }
    },

    /**
     * « Ventes en attente » → resume a draft (XD-46): into the active window when it is empty,
     * else into a new window (F3). Returns an error message or null.
     */
    reprendreVente: async (vente_id) => {
      let id = get().activeId;
      const f = id ? get().fenetres[id] : null;
      const vide = f && !f.lignes.length && !vue(id).nouvelles.length;
      if (!vide) {
        const err = await get().nouvelleFenetre();
        if (err) return err;
        id = get().activeId;
      }
      try {
        const r = donnees(await enFile(id, () => caisseFenetreApi.reprendreVente(id, vente_id)));
        installer(r.fenetre);
        set({ activeId: id });
        return null;
      } catch (e) {
        return lireRefus(e, "Reprise impossible").message;
      }
    },
  };
});

// ── sessionStorage mirror (per tab, per user) ───────────────────────────────────────────────────
useCaisseFenetresStore.subscribe((s) => {
  if (!s.userId || s.statut === "inactif") return;
  const vues = Object.fromEntries(Object.entries(s.vues).map(([k, v]) => [k, { meta: v.meta, quantites: v.quantites, remise: v.remise ?? null }]));
  ecrireStockage(
    session(),
    cleMiroir(s.userId),
    JSON.stringify({ sessionId: s.sessionId, fenetres: s.fenetres, activeId: s.activeId, expirees: s.expirees, vues, locaux: s.locaux }),
  );
});

export default useCaisseFenetresStore;
